'use client';
import { useCallback, useEffect, useRef } from 'react';

import axios from 'axios';
import { useRouter } from 'next/navigation';

import {
  useAuthState,
  useSetTokenExpirationDate,
} from '@contexts/auth/AuthContext';
import { callService } from '@api/remote-call';
import {
  getExpirationDate,
  saveTokenInfo,
} from '@components/views/login/utils';
import { REFRESH_TOKEN_KEY } from '@components/views/login/constants';
import { useCookies, useSetCookies } from '@contexts/cookies';
import useGetasPath from '@hooks/useGetasPath';
import {
  getSafeLocalStorageItem,
  setSafeLocalStorageItem,
} from '@utils/localStorage/getSafeLocalStorage';
import { useLogout } from '@hooks/useLogout';
import { captureAppException } from '@utils/exception-logger';

import { getLoginSessionExpirationDate } from './tokens';
import { AuthSyncType, useAuthTabSync } from './useAuthTabSync';
import { checkNetworkError } from './checkNetworkError';
import { retryWithBackoff } from './retryWithBackoff';
import { waitForNetworkRestoration } from './networkConnectivity';

let logoutTime: NodeJS.Timeout;
let tokenExpiresTime: NodeJS.Timeout;

const AuthTokenExpiration = () => {
  const { tokenExpirationDate } = useAuthState();
  const tokenExpirationDispatch = useSetTokenExpirationDate();
  const cookies = useCookies();
  const dispatch = useSetCookies();
  const { refresh } = useRouter();
  const asPath = useGetasPath();
  const { logout } = useLogout();
  const isRefreshing = useRef(false);
  const refreshNetworkError = useRef(false);
  const { broadcastAuthChange } = useAuthTabSync();

  const handleTokenExpiration = useCallback(async () => {
    if (isRefreshing.current) {
      return;
    }

    if (navigator?.onLine === false || document.visibilityState !== 'visible') {
      refreshNetworkError.current = true;
      return;
    }

    const refreshToken = getSafeLocalStorageItem(REFRESH_TOKEN_KEY);
    const userInfoData = getSafeLocalStorageItem('userInfo');
    const userInfo = userInfoData ? JSON.parse(userInfoData) : {};
    const userEmail = userInfo.userName;

    if (!userEmail || !refreshToken) {
      await logout();
      return;
    }

    isRefreshing.current = true;
    refreshNetworkError.current = false; // Reset network error flag on new attempt

    try {
      // Use retry mechanism for token refresh
      const tokenData = await retryWithBackoff(
        async () => {
          const result = await callService(
            {
              service: 'AuthService',
              method: 'tokenRefresh',
              version: 'v3',
            },
            { body: { refreshToken, email: userEmail ?? '' } }
          );

          // Check for network error code (e.g., ENOTFOUND)
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore-next-line
          if (result?.code === 'ENOTFOUND') {
            const networkError = new Error('Network connection failed');
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore-next-line
            networkError.code = 'ENOTFOUND';
            throw networkError;
          }

          if (!result?.token) {
            const authError = new Error(
              'Token refresh failed - invalid response'
            );
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore-next-line
            authError.isAuthError = true;
            throw authError;
          }

          return result;
        },
        {
          maxAttempts: 3,
          baseDelay: 2000,
          maxDelay: 8000,
          shouldRetry: (error: unknown) => {
            const isNetworkError = checkNetworkError(error);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore-next-line
            const isAuthError = error?.isAuthError === true;

            // Only retry network errors, not auth errors
            return isNetworkError && !isAuthError;
          },
        }
      );

      saveTokenInfo(tokenData);
      dispatch({
        type: 'SET_COOKIE',
        key: 'token',
        value: `${tokenData.tokenType} ${tokenData.token}`,
      });

      await axios.post('/api/token', {
        tokenType: tokenData.tokenType,
        token: tokenData.token,
        maxAge: tokenData.expires,
      });

      setSafeLocalStorageItem(
        'token',
        `${tokenData.tokenType} ${tokenData.token}`
      );

      const newExpirationDate = getExpirationDate(tokenData.expires);
      tokenExpirationDispatch(newExpirationDate);

      broadcastAuthChange(AuthSyncType.LOGIN);

      refresh();
    } catch (error: unknown) {
      captureAppException(error, 'AuthTokenRefresh', {
        pathname:
          typeof window !== 'undefined'
            ? window?.location?.pathname
            : undefined,
        attemptType: 'refreshToken',
        wasNetworkFlagSet: refreshNetworkError?.current,
      });
      const isNetworkError = checkNetworkError(error);
      if (isNetworkError) {
        refreshNetworkError.current = true;
      } else {
        await logout();
      }
    } finally {
      isRefreshing.current = false;
    }
  }, [logout, dispatch, tokenExpirationDispatch, broadcastAuthChange, refresh]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (
        document.visibilityState === 'visible' &&
        refreshNetworkError.current
      ) {
        // Wait for network to be restored before attempting token refresh
        const isNetworkRestored = await waitForNetworkRestoration({
          maxAttempts: 5,
          checkInterval: 1000,
        });

        if (isNetworkRestored) {
          handleTokenExpiration();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleTokenExpiration]);

  useEffect(() => {
    const handleTabActivity = () => {
      if (tokenExpirationDate) {
        const expirationTimeWithBuffer = tokenExpirationDate.getTime() - 5000;
        if (expirationTimeWithBuffer <= new Date().getTime()) {
          handleTokenExpiration();
        }
      }
    };

    window.addEventListener('focus', handleTabActivity);
    window.addEventListener('pageshow', handleTabActivity);

    return () => {
      window.removeEventListener('focus', handleTabActivity);
      window.removeEventListener('pageshow', handleTabActivity);
    };
  }, [tokenExpirationDate, handleTokenExpiration]);

  useEffect(() => {
    clearTimeout(tokenExpiresTime);
    if (tokenExpirationDate) {
      const timeoutDelay = tokenExpirationDate.getTime() - new Date().getTime();
      const delayedTokenExpirationTime = Math.max(0, timeoutDelay - 5000);

      tokenExpiresTime = setTimeout(
        () => handleTokenExpiration(),
        delayedTokenExpirationTime
      );
    }
    return () => clearTimeout(tokenExpiresTime);
  }, [tokenExpirationDate, handleTokenExpiration]);

  useEffect(() => {
    const LLToken = getSafeLocalStorageItem('token');
    if (cookies?.token && LLToken && cookies.token !== LLToken) {
      handleTokenExpiration().then(() => {
        refresh();
      });
    }
  }, [cookies.token, handleTokenExpiration, refresh]);

  useEffect(() => {
    clearTimeout(logoutTime);
    const { loginSessionExpiration, loginSessionExpirationDate } =
      getLoginSessionExpirationDate();

    if (loginSessionExpiration) {
      const inMs = loginSessionExpirationDate.getTime() - new Date().getTime();
      logoutTime = setTimeout(() => {
        logout(asPath);
      }, inMs);
    }
    return () => clearTimeout(logoutTime);
  }, [logout, asPath]);

  return null;
};

export default AuthTokenExpiration;
